import { DexieDb, type Db } from '@shared/storage';
import { Article, Issue, Style } from '@/constants';
import { Logger } from '@shared/lib/logger';

import { loadDownloadedIssueStyles } from './downloaded-issue';

const logger = new Logger('downloaded-issue-loader-cache');

/**
 * Prefetch for the `downloads/issues/$issueId` route's `loader`
 * (`templates/basic/app.router.tsx`) -- the Dexie read this route needs is
 * normally done in a `useEffect` by `useDownloadedIssueView`, since Dexie
 * has no TanStack Query counterpart to hang a route loader off of the way
 * `previewViewRoute`'s loader does (`app/routing/route-catalog.tsx`).
 * Without this, TanStack Router still starts the cover-morph view
 * transition (`PreviewLink`, `@shared/ui/preview-link`) right away, but its
 * "after" snapshot is only ever taken once the route's loader has resolved
 * -- with no loader at all, that's effectively instantly, always capturing
 * the transient `<Loading />` screen instead of the real reader with its
 * matching-`view-transition-name` cover, so the browser has nothing to
 * morph the list cover into and falls back to a generic cross-fade.
 *
 * A second, standalone `DexieDb` connection rather than reusing
 * `AppContext`'s -- a route loader runs outside the React tree (no
 * `useContext`), and `AppContext`'s own instance isn't guaranteed open yet
 * this early in app boot anyway. Dexie multiplexes multiple connections to
 * the same named database safely, so this can't drift from what
 * `useDownloadedIssueView`'s own `db` reads/writes moments later.
 */
let dbPromise: Promise<Db> | null = null;

function getDb(): Promise<Db> {
	if (!dbPromise) {
		const db = new DexieDb();
		dbPromise = db.start().then(() => db);
	}
	return dbPromise;
}

const cache = new Map<string, DownloadedIssuePrefetch>();

export interface DownloadedIssuePrefetch {
	issue: Issue;
	articles: Array<Article>;
	// Read here too, not left to a later effect: the reader's first paint
	// needs the styles as much as the articles, or it renders them unstyled
	// for a frame (the flash this exists to prevent) and the cover-morph's
	// "after" snapshot captures that unstyled frame.
	stylesMap: Map<string, Style>;
}

/**
 * Awaited by the route `loader` -- resolves once the issue (tried as a
 * primary key, then as a slug, the same fallback order
 * `useDownloadedIssueView`'s own effect uses), its articles and its styles
 * are read from Dexie, caching the result for `takeDownloadedIssuePrefetch` to pick
 * up. Never throws: "not downloaded" is a real, already-handled UI state
 * (`NotDownloaded`), not a loader failure -- a rejected loader would
 * instead surface as a router-level crash screen.
 */
export async function prefetchDownloadedIssue(issueId: string): Promise<void> {
	try {
		const db = await getDb();
		let found = await db.getIssue(issueId);
		if (!found) {
			found = await db.getIssue(issueId, true);
		}
		if (!found) {
			return;
		}
		const articles = await db.getArticlesByIssue(found.id);
		const stylesMap = await loadDownloadedIssueStyles(db, found, articles);
		cache.set(issueId, { issue: found, articles, stylesMap });
	} catch (error) {
		logger.error('Failed to prefetch downloaded issue', error);
	}
}

/**
 * Consumed exactly once, by `useDownloadedIssueView`'s initial state --
 * seeds it synchronously so this route's first paint already shows real
 * content instead of `<Loading />`, matching what the loader above already
 * guaranteed had happened before this component ever mounted. Popped (not
 * just read) so a later revisit or `reloadIssue()` always re-reads live
 * Dexie state through the normal effect, never a stale snapshot left over
 * from a previous navigation.
 */
export function takeDownloadedIssuePrefetch(
	issueId: string | undefined,
): DownloadedIssuePrefetch | null {
	if (!issueId) {
		return null;
	}
	const found = cache.get(issueId);
	if (!found) {
		return null;
	}
	cache.delete(issueId);
	return found;
}
